'use client';

import * as React from 'react';

/** Visual style of the button. `tsuyosa` is the brand pink with glow effect. */
export type ButtonVariant = 'default' | 'outline' | 'ghost' | 'tsuyosa';

/** `icon` renders a square button (no children rendered, use `leftIcon`). */
export type ButtonSize = 'sm' | 'md' | 'lg' | 'icon';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:   ButtonVariant;
  size?:      ButtonSize;
  /** Shows a spinner and sets `disabled` + `aria-busy`. Replaces `leftIcon` while active. */
  isLoading?: boolean;
  /** Icon rendered before children. Hidden while `isLoading`. */
  leftIcon?:  React.ReactNode;
  /** Icon rendered after children. Hidden while `isLoading`. */
  rightIcon?: React.ReactNode;
  /** Stretches button to full container width. */
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  default:
    'bg-[#F5F5F5] text-[#0A0A0A] border border-[#D4D4D4] hover:bg-white hover:border-[#A3A3A3]',
  outline: 'bg-transparent border border-current text-[#F5F5F5] hover:bg-white/10',
  ghost:   'bg-transparent text-[#F5F5F5] hover:bg-white/10',
  tsuyosa:
    'bg-[#F472B6] text-white border border-[#EC4899] hover:bg-[#EC4899] ' +
    'shadow-[0_0_12px_rgba(244,114,182,0.35)] hover:shadow-[0_0_20px_rgba(244,114,182,0.6)]',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm:   'h-8 px-3 py-1.5 text-sm gap-1.5',
  md:   'h-10 px-5 py-2.5 text-base gap-2',
  lg:   'h-12 px-7 py-3 text-lg gap-2.5',
  icon: 'h-10 w-10 p-0',
};

const BASE =
  'inline-flex items-center justify-center rounded-md font-medium ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#F472B6] ' +
  'disabled:pointer-events-none disabled:opacity-40 transition-all duration-200 cursor-pointer select-none';

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'default', size = 'md', isLoading = false, fullWidth = false,
     leftIcon, rightIcon, className = '', disabled, children, ...rest }, ref) => {
    const cls = [BASE, variantClasses[variant], sizeClasses[size], fullWidth ? 'w-full' : '', className]
      .filter(Boolean).join(' ');

    return (
      <button ref={ref} disabled={disabled ?? isLoading} aria-busy={isLoading} className={cls} {...rest}>
        {isLoading ? (
          <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          leftIcon && <span className="shrink-0">{leftIcon}</span>
        )}
        {size !== 'icon' && children}
        {!isLoading && rightIcon && <span className="shrink-0">{rightIcon}</span>}
      </button>
    );
  }
);
Button.displayName = 'Button';
